import { EyeOff, LockIcon, Users2Icon, PlusIcon } from "lucide-react";
import { ComponentProps, Fragment, PropsWithChildren, forwardRef } from "react";

import { AvailableIntegrationFragment } from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import IconWithBG from "@/components/elements/IconWithBG";
import { PlanFeatureBadge } from "@/components/elements/PlanFeatureBadge";
import Tooltip from "@/components/elements/Tooltip";
import { formatPlanName, parsePlanName } from "@/features/billing";
import { ConnectorOption as ConnectorOptionType } from "@/features/connectors";
import cn from "@/helpers/classNames";
import { IntegrationLogoBox } from "@/integrations";

import { useConnectorsContext } from "../ConnectorsProvider";
import {
  ConnectorCard,
  ConnectorCardContent,
  ConnectorCardLabels,
  ConnectorCardTitle,
} from "./ConnectorCard";
import { ExistingConnectionSelect } from "./ExistingConnectionSelect";
import { abilityFilters } from "./constants";

export function getMinRequiredPlanLabel(
  metadata?: AvailableIntegrationFragment,
  showPlanRequirement?: boolean,
) {
  if (
    metadata != null &&
    (metadata.meetsPlanRequirement === false || showPlanRequirement) &&
    metadata.minRequiredPlan != null
  ) {
    return parsePlanName(metadata.minRequiredPlan);
  }
  return null;
}

export function ConnectorOption(
  props: PropsWithChildren<{
    option: ConnectorOptionType;
    showAbilityLabels?: boolean;
    showPlanRequirement?: boolean;
  }>,
) {
  const { option } = props;

  const {
    onSelectIntegration,
    onSelectConnection,
    getExistingConnections,
    enableSelectExistingConnection,
  } = useConnectorsContext();

  let existingConnections = getExistingConnections(option.value);
  // Make sure DW connection is not shown for the BigQuery connector
  if (option.value === "bigquery") {
    existingConnections = existingConnections.filter(
      (x) =>
        x.label !== "_datawarehouse" && x.label !== "_datawarehouse-previous",
    );
  }
  const hasExistingConnections = existingConnections.length > 0;

  const minRequiredPlan = getMinRequiredPlanLabel(
    props.option.metadata,
    props.showPlanRequirement,
  );

  const showSoonLabelAsBadge = minRequiredPlan == null;

  return (
    <Tooltip
      content={
        minRequiredPlan
          ? `${option.label} is available on the ${formatPlanName(minRequiredPlan)} plan.`
          : null
      }
    >
      <ConnectorCard
        key={option.value}
        icon={<IntegrationLogoBox id={option.value} size="sm" />}
        instanceCount={existingConnections.length}
        isLocked={option.disabled}
        onClick={() => onSelectIntegration(option)}
      >
        <ConnectorCardContent>
          <div className="flex items-center gap-2">
            <ConnectorCardTitle title={option.label}>
              {option.label}
            </ConnectorCardTitle>
            <ConnectorCardLabels className="flex-1">
              {minRequiredPlan ? (
                <PlanFeatureBadge planName={minRequiredPlan} />
              ) : showSoonLabelAsBadge && option.isSoonConnector ? (
                <Badge variant="outline">Soon</Badge>
              ) : null}
            </ConnectorCardLabels>
          </div>
          <MetadataLabels
            option={option}
            showAbilityLabels={props.showAbilityLabels}
            includeSoonLabels={!showSoonLabelAsBadge}
          />
        </ConnectorCardContent>
        {hasExistingConnections && enableSelectExistingConnection && (
          <div
            onClick={(e) => {
              // Prevent click event from bubbling up to the parent ConnectorCard
              e.stopPropagation();
              e.preventDefault();
            }}
            className="text-sm opacity-0 transition-opacity duration-200 group-focus-within:opacity-100 group-hover:opacity-100"
          >
            <ExistingConnectionSelect
              options={existingConnections ?? []}
              onChange={(connection) => {
                onSelectConnection?.(connection);
              }}
            >
              <ConnectorCardOverlayButton position="top">
                <IntegrationLogoBox id={option.integration.id} size="xs" />
                <span className="truncate">
                  Use existing connection{" "}
                  {existingConnections.length > 1
                    ? `(${existingConnections.length})`
                    : ""}
                </span>
              </ConnectorCardOverlayButton>
            </ExistingConnectionSelect>
            <ConnectorCardOverlayButton
              position="bottom"
              onClick={(e) => {
                e.stopPropagation();
                onSelectIntegration(option);
              }}
            >
              <IconWithBG size="xs" icon={<PlusIcon className="h-5" />} />
              <span className="truncate">Create new connection</span>
            </ConnectorCardOverlayButton>
          </div>
        )}
      </ConnectorCard>
    </Tooltip>
  );
}

const ConnectorCardOverlayButton = forwardRef<
  HTMLDivElement,
  ComponentProps<"div"> & {
    position: "top" | "bottom";
  }
>((props, ref) => {
  const { position, children, ...rest } = props;
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      className={cn(
        "absolute left-0 flex h-1/2 w-full items-center gap-4 bg-blue-50 px-5 text-foreground transition-all duration-200 hover:bg-blue-100 focus:bg-blue-100 active:bg-blue-200 dark:bg-background dark:hover:bg-gray-700 dark:focus:bg-gray-700 dark:active:bg-gray-600",
        "group-focus-within:translate-y-0 group-hover:translate-y-0",
        position === "top" && "top-0 left-0 -translate-y-full",
        position === "bottom" &&
          "bottom-0 left-0 translate-y-full border-t border-blue-200 dark:border-gray-500",
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

function MetadataLabels(props: {
  option: ConnectorOptionType;
  showAbilityLabels?: boolean;
  includeSoonLabels?: boolean;
}) {
  const labels: { label: string; icon?: React.ReactElement }[] = [];
  if (props.showAbilityLabels) {
    for (const ability of props.option.abilities) {
      const label = abilityFilters.find((x) => x.value === ability)?.label;
      if (label) {
        labels.push({ label });
      }
    }
  }

  if (props.option.isBeta) {
    labels.push({ label: "Beta" });
  }

  if (props.includeSoonLabels && props.option.isSoonConnector) {
    labels.push({ label: "Soon" });
  }

  if (props.option.isCommunity) {
    labels.push({
      label: "Community",
      icon: <Users2Icon className="size-3" />,
    });
  }

  if (props.option.isInsufficientAdminRole) {
    labels.push({
      label: "Requires Admin",
      icon: <LockIcon className="size-3" />,
    });
  }

  if (props.option.isPreview) {
    labels.push({
      label: "Priv. Preview",
      icon: <EyeOff className="size-3" />,
    });
  }

  if (labels.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center text-xs text-muted-foreground">
      {labels.map((label, index) => {
        return (
          <Fragment key={label.label}>
            <span className="inline-flex items-center gap-1">
              {label.icon}
              {label.label}
            </span>
            {index < labels.length - 1 && <span className="mx-1">•</span>}
          </Fragment>
        );
      })}
    </div>
  );
}
